import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { SiteTariff } from 'clipsal-cortex-types/src/api/api-tariffs-v2';

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Computes the default effective date for a new tariff.
 *
 * The effective date defaults to today's date in the site's timezone. Because a site's tariff
 * effective dates must be unique, if today already has a tariff the date is advanced one day at a
 * time until it no longer collides with an existing tariff.
 *
 * Using the site timezone (rather than the device's) keeps the date correct for users configuring a
 * tariff from a different timezone than the site. Dates are parsed/formatted as plain `yyyy-MM-dd`
 * strings to avoid the UTC shift that `new Date('yyyy-MM-dd')` would introduce.
 *
 * @param existingTariffs - the site's existing tariffs, used to avoid effective date collisions
 * @param timezone - the site's IANA timezone (falls back to UTC when missing)
 * @returns the effective date as a `yyyy-MM-dd` string
 */
export const getDefaultEffectiveDate = (
  existingTariffs: Pick<SiteTariff, 'tariff_effective_date'>[],
  timezone: string | null | undefined
): string => {
  const existingDates = new Set(existingTariffs.map((tariff) => tariff.tariff_effective_date));
  const today = formatInTimeZone(new Date(), timezone || 'UTC', DATE_FORMAT);
  const candidate = parse(today, DATE_FORMAT, new Date());

  let candidateDate = format(candidate, DATE_FORMAT);
  while (existingDates.has(candidateDate)) {
    candidate.setDate(candidate.getDate() + 1);
    candidateDate = format(candidate, DATE_FORMAT);
  }

  return candidateDate;
};
